import { useEffect, useMemo, useState } from 'react';
import { api, FetchError } from '../api.ts';
import { Spinner } from '../components/Spinner.tsx';
import { Deck } from '@sab/slides';
import '../../../../../packages/core/src/tokens/tokens.css';
import '../../../../../packages/slides/src/styles.css';
import type { Proposal, Slide } from '@sab/core/schemas';

interface EditorProps {
  proposalId: string;
  publicBaseUrl: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; proposal: Proposal };

export function Editor({ proposalId, publicBaseUrl }: EditorProps) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [activeIndex, setActiveIndex] = useState(0);
  const [working, setWorking] = useState<
    null | 'saving' | 'publishing' | 'regenerating' | 'undoing'
  >(null);
  const [instruction, setInstruction] = useState('');
  // Per-slide undo budget — server keeps up to 3 history rows per slide.
  const [undoCountByIndex, setUndoCountByIndex] = useState<Record<number, number>>({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    api
      .get(proposalId)
      .then(async ({ proposal }) => {
        if (cancelled) return;
        setState({ kind: 'ready', proposal });
        // Fetch per-slide undo budget so reopening the editor shows the
        // correct "X/3 versi" pill on slides that have history.
        try {
          const summary = await api.slideHistorySummary(proposalId);
          if (!cancelled) setUndoCountByIndex(summary.countsByIndex);
        } catch {
          /* non-fatal — undo just shows 0 until user regenerates */
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof FetchError ? err.body.error : 'Gagal memuat';
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const sortedSlides = useMemo<Slide[]>(() => {
    if (state.kind !== 'ready') return [];
    return [...state.proposal.slides].sort((a, b) => a.index - b.index);
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <section className="page">
        <Spinner label="Memuat proposal" />
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="page">
        <div className="alert alert--error" role="alert">
          {state.message}
        </div>
      </section>
    );
  }

  const proposal = state.proposal;
  const active = sortedSlides[Math.min(activeIndex, sortedSlides.length - 1)];

  const handlePublish = async () => {
    setWorking('publishing');
    try {
      const res = await api.publish(proposal.id);
      setState({ kind: 'ready', proposal: res.proposal });
    } catch (err) {
      const message = err instanceof FetchError ? err.body.error : 'Gagal publish';
      setState({ kind: 'error', message });
    } finally {
      setWorking(null);
    }
  };

  const handleRegenerate = async () => {
    if (!active || !instruction.trim()) return;
    setWorking('regenerating');
    try {
      const res = await api.regenerateSlide(proposal.id, active.index, instruction.trim());
      setState({ kind: 'ready', proposal: res.proposal });
      setInstruction('');
      setUndoCountByIndex((prev) => ({
        ...prev,
        [active.index]: typeof res.undoAvailable === 'number' ? res.undoAvailable : 1,
      }));
    } catch (err) {
      const message = err instanceof FetchError ? err.body.error : 'Gagal regenerate';
      alert(message);
    } finally {
      setWorking(null);
    }
  };

  const handleUndo = async () => {
    if (!active) return;
    const currentCount = undoCountByIndex[active.index] ?? 0;
    if (currentCount <= 0) return;
    setWorking('undoing');
    try {
      const res = await api.undoSlide(proposal.id, active.index);
      setState({ kind: 'ready', proposal: res.proposal });
      setUndoCountByIndex((prev) => ({ ...prev, [active.index]: res.undoAvailable }));
    } catch (err) {
      const message = err instanceof FetchError ? err.body.error : 'Gagal undo';
      alert(message);
    } finally {
      setWorking(null);
    }
  };

  const publicUrl =
    proposal.status === 'published' ? `${publicBaseUrl.replace(/\/$/, '')}/${proposal.slug}` : null;

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  const activeUndoCount = active ? (undoCountByIndex[active.index] ?? 0) : 0;

  return (
    <section className="page page--editor" aria-labelledby="editor-title">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">Editor</p>
          <h1 id="editor-title" className="page__title">
            {proposal.prospect.companyName}
          </h1>
          <p className="page__lede">
            Slug: <code>{proposal.slug}</code> · Status:{' '}
            <span className={`badge badge--${proposal.status}`}>{proposal.status}</span>
          </p>
        </div>
        <div className="form__actions">
          <button type="button" className="btn" onClick={handlePublish} disabled={working !== null}>
            {working === 'publishing' ? (
              <Spinner label="Publish" size="sm" />
            ) : proposal.status === 'published' ? (
              'Re-publish'
            ) : (
              'Publish'
            )}
          </button>
        </div>
      </header>

      {publicUrl ? (
        <aside className="public-link" role="group" aria-label="Tautan publik proposal">
          <div className="public-link__head">
            <span className="public-link__dot" aria-hidden="true" />
            <span className="public-link__label">Live · Tautan publik</span>
          </div>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener"
            className="public-link__url"
            title="Buka di tab baru"
          >
            {publicUrl.replace(/^https?:\/\//, '')}
          </a>
          <div className="public-link__actions">
            <button
              type="button"
              className="public-link__btn"
              onClick={copyPublicUrl}
              aria-live="polite"
              title={copied ? 'Tersalin' : 'Salin tautan'}
            >
              {copied ? (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                  <path
                    d="M5 12l4.5 4.5L19 7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                  <rect
                    x="9"
                    y="9"
                    width="11"
                    height="11"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M5 15V6a2 2 0 0 1 2-2h9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              <span>{copied ? 'Tersalin' : 'Salin'}</span>
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener"
              className="public-link__btn public-link__btn--primary"
              title="Buka di tab baru"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                <path
                  d="M14 4h6v6M10 14L20 4M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Buka</span>
            </a>
          </div>
        </aside>
      ) : null}

      <div className="editor">
        <SlideList
          slides={sortedSlides}
          activeIndex={activeIndex}
          onActivate={(idx) => setActiveIndex(idx)}
          /* Optimistic: flip local state instantly, send server request in
           * background, rollback on error. Zero perceived latency. */
          onReorder={(orderIds) => {
            const before = proposal;
            const byId = new Map(before.slides.map((s) => [s.id, s]));
            const optimistic: Proposal = {
              ...before,
              slides: orderIds.map((sid, i) => ({ ...byId.get(sid)!, index: i })),
            };
            setState({ kind: 'ready', proposal: optimistic });
            const stillActiveIdx = optimistic.slides.findIndex((s) => s.id === active?.id);
            if (stillActiveIdx >= 0) setActiveIndex(stillActiveIdx);
            void api.reorderSlides(before.id, orderIds).catch((err: unknown) => {
              setState({ kind: 'ready', proposal: before }); // rollback
              const message = err instanceof FetchError ? err.body.error : 'Gagal reorder';
              alert(`Reorder gagal — perubahan di-rollback.\n${message}`);
            });
          }}
          onDelete={(slideId) => {
            if (!window.confirm('Hapus slide ini? Aksi tidak bisa di-undo.')) return;
            const before = proposal;
            const remaining = before.slides
              .filter((s) => s.id !== slideId)
              .map((s, i) => ({ ...s, index: i }));
            if (remaining.length === 0) return; // backend would 409 anyway
            const optimistic: Proposal = { ...before, slides: remaining };
            setState({ kind: 'ready', proposal: optimistic });
            setActiveIndex((i) => Math.min(i, remaining.length - 1));
            void api.deleteSlide(before.id, slideId).catch((err: unknown) => {
              setState({ kind: 'ready', proposal: before });
              const message = err instanceof FetchError ? err.body.error : 'Gagal hapus';
              alert(`Hapus gagal — slide dikembalikan.\n${message}`);
            });
          }}
          onInsertAfter={(afterIndex) => {
            const before = proposal;
            // Optimistic placeholder — temp UUID; server will return the
            // canonical proposal which replaces this on success.
            const tmpId = `tmp-${crypto.randomUUID()}`;
            const placeholder = {
              id: tmpId,
              index: 0,
              type: 'custom' as const,
              content: {
                eyebrow: undefined,
                headline: 'Slide Baru',
                subheadline: undefined,
                bullets: [],
                body: undefined,
                metrics: [],
              },
            };
            const insertAt = afterIndex === null ? 0 : afterIndex + 1;
            const optimistic: Proposal = {
              ...before,
              slides: [
                ...before.slides.slice(0, insertAt),
                placeholder,
                ...before.slides.slice(insertAt),
              ].map((s, i) => ({ ...s, index: i })),
            };
            setState({ kind: 'ready', proposal: optimistic });
            setActiveIndex(insertAt);
            void api
              .insertSlide(before.id, afterIndex, 'custom')
              .then((res) => {
                setState({ kind: 'ready', proposal: res.proposal });
                setActiveIndex(res.insertedAt);
              })
              .catch((err: unknown) => {
                setState({ kind: 'ready', proposal: before });
                const message = err instanceof FetchError ? err.body.error : 'Gagal insert';
                alert(`Insert gagal — slide dibatalkan.\n${message}`);
              });
          }}
        />

        <div className="editor__stage">
          <div className="editor__preview sab-deck-host" data-stage="single">
            {active ? (
              <Deck
                proposal={{ ...proposal, slides: [active] }}
                contactEmail="hello@sab.id"
                contactName="Tri Lastiko (Tico)"
              />
            ) : null}
          </div>
          <div className="editor__controls">
            <label htmlFor="instruction" className="field__label">
              Instruksi untuk slide ini (regenerate dengan AI)
            </label>
            <textarea
              id="instruction"
              className="field__input field__input--area"
              rows={3}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="cth: Buat lebih singkat, tekankan ROI dalam 30 hari…"
              disabled={working !== null}
            />
            <div className="form__actions form__actions--split">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={working !== null || activeUndoCount <= 0}
                onClick={handleUndo}
                title={
                  activeUndoCount > 0
                    ? `Undo terakhir (${activeUndoCount} versi tersedia)`
                    : 'Tidak ada versi sebelumnya'
                }
              >
                {working === 'undoing' ? (
                  <Spinner label="Undo" size="sm" />
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                      <path
                        d="M9 14l-5-5 5-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 9h11a5 5 0 0 1 0 10h-3"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Undo</span>
                    {activeUndoCount > 0 ? (
                      <span className="btn__pill" aria-label={`${activeUndoCount} versi tersedia`}>
                        {activeUndoCount}/3
                      </span>
                    ) : null}
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn--accent"
                disabled={working !== null || !instruction.trim()}
                onClick={handleRegenerate}
              >
                {working === 'regenerating' ? (
                  <Spinner label="Regenerate" size="sm" />
                ) : (
                  'Regenerate Slide'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SlideList — sidebar with drag–drop reorder + ↑↓ arrows + delete + insert
   God-tier UX: works keyboard-only, touch-friendly (44×44 targets on mobile),
   instant optimistic feedback, undo-by-reload (server is authoritative).
   ============================================================ */

const SLIDE_TYPE_LABELS: Record<string, string> = {
  cover: 'Cover',
  opportunity: 'Peluang',
  problem: 'Masalah',
  'why-matters': 'Dampak',
  'sab-intro': 'Tentang SAB',
  process: 'Proses',
  'case-study': 'Studi Kasus',
  services: 'Layanan',
  difference: 'Differensiasi',
  approach: 'Pendekatan',
  strategy: 'Strategi',
  'expected-results': 'Hasil',
  pricing: 'Paket & Investasi',
  'why-sab': 'Kenapa SAB',
  timeline: 'Timeline',
  'next-steps': 'Langkah Selanjutnya',
  contact: 'Kontak',
  appendix: 'Lampiran',
  custom: 'Slide',
};

interface SlideListProps {
  slides: Slide[];
  activeIndex: number;
  onActivate: (index: number) => void;
  onReorder: (orderIds: string[]) => void;
  onDelete: (slideId: string) => void;
  onInsertAfter: (afterIndex: number | null) => void;
}

function SlideList({
  slides,
  activeIndex,
  onActivate,
  onReorder,
  onDelete,
  onInsertAfter,
}: SlideListProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    id: string;
    position: 'above' | 'below';
  } | null>(null);

  const reorderById = (sourceId: string, targetId: string, before: boolean) => {
    if (sourceId === targetId) return;
    const ids = slides.map((s) => s.id);
    const fromIdx = ids.indexOf(sourceId);
    if (fromIdx === -1) return;
    ids.splice(fromIdx, 1);
    let toIdx = ids.indexOf(targetId);
    if (toIdx === -1) return;
    if (!before) toIdx += 1;
    ids.splice(toIdx, 0, sourceId);
    onReorder(ids);
  };

  const move = (slideId: string, direction: -1 | 1) => {
    const idx = slides.findIndex((s) => s.id === slideId);
    if (idx === -1) return;
    const target = idx + direction;
    if (target < 0 || target >= slides.length) return;
    const ids = slides.map((s) => s.id);
    [ids[idx], ids[target]] = [ids[target]!, ids[idx]!];
    onReorder(ids);
  };

  return (
    <aside className="editor__sidebar" aria-label="Daftar slide">
      <header className="slide-list__head">
        <span className="slide-list__eyebrow">Daftar Slide</span>
        <span className="slide-list__count">{slides.length}</span>
      </header>
      <ol className="slide-list">
        {/* Insert button BEFORE the first slide */}
        <li>
          <InsertRow onInsert={() => onInsertAfter(null)} disabled={slides.length >= 50} />
        </li>

        {slides.map((s, i) => {
          const isActive = activeIndex === i;
          const isDragSource = dragId === s.id;
          const dropPos = dropTarget?.id === s.id ? dropTarget.position : undefined;
          return (
            <li key={s.id}>
              <div
                className={[
                  'slide-list__row',
                  isActive ? 'is-active' : '',
                  isDragSource ? 'is-dragging' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-drop={dropPos}
                draggable
                onDragStart={(e) => {
                  setDragId(s.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', s.id);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setDropTarget(null);
                }}
                onDragOver={(e) => {
                  if (!dragId || dragId === s.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const above = e.clientY < rect.top + rect.height / 2;
                  const nextPos: 'above' | 'below' = above ? 'above' : 'below';
                  if (dropTarget?.id !== s.id || dropTarget.position !== nextPos) {
                    setDropTarget({ id: s.id, position: nextPos });
                  }
                }}
                onDragLeave={() => {
                  if (dropTarget?.id === s.id) setDropTarget(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceId = e.dataTransfer.getData('text/plain') || dragId;
                  if (sourceId && sourceId !== s.id) {
                    const before = dropTarget?.position === 'above';
                    reorderById(sourceId, s.id, before);
                  }
                  setDragId(null);
                  setDropTarget(null);
                }}
              >
                <button
                  type="button"
                  className="slide-list__handle"
                  aria-label="Tahan untuk drag-reorder slide"
                  tabIndex={-1}
                  title="Drag untuk reorder"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                    <circle cx="9" cy="6" r="1.3" fill="currentColor" />
                    <circle cx="9" cy="12" r="1.3" fill="currentColor" />
                    <circle cx="9" cy="18" r="1.3" fill="currentColor" />
                    <circle cx="15" cy="6" r="1.3" fill="currentColor" />
                    <circle cx="15" cy="12" r="1.3" fill="currentColor" />
                    <circle cx="15" cy="18" r="1.3" fill="currentColor" />
                  </svg>
                </button>

                <button
                  type="button"
                  className="slide-list__main"
                  onClick={() => onActivate(i)}
                  aria-pressed={isActive}
                  aria-label={`Buka slide ${i + 1}: ${s.content.headline}`}
                >
                  <span className="slide-list__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="slide-list__body">
                    <span className="slide-list__title">
                      {s.content.headline || '— belum diisi'}
                    </span>
                    <span className="slide-list__type">{SLIDE_TYPE_LABELS[s.type] ?? s.type}</span>
                  </span>
                </button>

                <div className="slide-list__actions" role="group" aria-label="Aksi slide">
                  <button
                    type="button"
                    className="slide-list__act"
                    onClick={() => move(s.id, -1)}
                    disabled={i === 0}
                    title="Naikkan slide (↑)"
                    aria-label="Naikkan slide"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                      <path
                        d="M6 15l6-6 6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="slide-list__act"
                    onClick={() => move(s.id, 1)}
                    disabled={i === slides.length - 1}
                    title="Turunkan slide (↓)"
                    aria-label="Turunkan slide"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="slide-list__act slide-list__act--danger"
                    onClick={() => onDelete(s.id)}
                    disabled={slides.length <= 1}
                    title={slides.length <= 1 ? 'Tidak bisa hapus slide terakhir' : 'Hapus slide'}
                    aria-label="Hapus slide"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                      <path
                        d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <InsertRow onInsert={() => onInsertAfter(i)} disabled={slides.length >= 50} />
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

/** Thin insert affordance — invisible until row is hovered/focused. */
function InsertRow({ onInsert, disabled }: { onInsert: () => void; disabled?: boolean }) {
  return (
    <div className="slide-list__insert">
      <button
        type="button"
        className="slide-list__insert-btn"
        onClick={onInsert}
        disabled={disabled}
        aria-label="Sisipkan slide baru di sini"
        title="Sisipkan slide baru"
      >
        <span className="slide-list__insert-line" aria-hidden="true" />
        <span className="slide-list__insert-chip" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Slide baru
        </span>
      </button>
    </div>
  );
}
